import { ref, computed, watch } from 'vue'
import { Capacitor } from '@capacitor/core'
import { SplashScreen } from '@capacitor/splash-screen'
import { useStoreLists } from '@/stores/storeLists'
import { useStoreAuth } from '@/stores/storeAuth'

// Global state for splash screen management
const splashDismissed = ref(false)
const circleListLoaded = ref(false)
const splashTimeout = ref(null)
const showCustomSpinner = ref(false)

// Maximum time to wait for splash screen (fallback timeout)
const MAX_SPLASH_DURATION = 5000 // 5 seconds

export function useSplashScreen() {
  const isIOS = computed(() => {
    return Capacitor.getPlatform() === 'ios'
  })

  const isNativeApp = computed(() => {
    return Capacitor.getPlatform() !== 'web'
  })

  const shouldManageSplash = computed(() => {
    return isNativeApp.value && isIOS.value
  })

  const canDismissSplash = computed(() => {
    return circleListLoaded.value || splashDismissed.value
  })

  /**
   * Check if circle list is already loaded and ready
   */
  const isCircleListReady = () => {
    const storeAuth = useStoreAuth()
    const storeLists = useStoreLists()

    // Check if user is authenticated first
    if (!storeAuth.user) {
      console.log('User not authenticated, cannot check circle list')
      return false
    }

    const circleList = storeLists.getSystemListByType('circle')

    if (!circleList) {
      console.log('Circle list not found in store')
      return false
    }

    const circleItems = storeLists.getListItemsByListId(circleList.id)
    const hasItems = circleItems && circleItems.length > 0

    console.log('Circle list check:', {
      userAuthenticated: !!storeAuth.user,
      listExists: !!circleList,
      listId: circleList?.id,
      itemsCount: circleItems?.length || 0,
      hasItems
    })

    return hasItems
  }

  /**
   * Initialize splash screen management
   * Only runs on iOS native apps
   */
  const initSplashScreen = () => {
    if (!shouldManageSplash.value) {
      // For non-iOS platforms, dismiss immediately
      dismissSplash()
      return
    }

    // Check if circle list is already loaded
    if (isCircleListReady()) {
      console.log('Circle list already loaded, dismissing splash immediately')
      markCircleListLoaded()
      return
    }

    // Show custom spinner overlay
    showCustomSpinner.value = true

    // Set up fallback timeout
    splashTimeout.value = setTimeout(() => {
      console.log('Splash screen timeout reached, dismissing...')
      dismissSplash()
    }, MAX_SPLASH_DURATION)

    // Watch for circle list to become available
    const storeAuth = useStoreAuth()
    const storeLists = useStoreLists()
    const stopWatching = watch(
      () => {
        // Only watch if user is authenticated
        if (!storeAuth.user) return null

        const circleList = storeLists.getSystemListByType('circle')
        if (!circleList) return null
        return storeLists.getListItemsByListId(circleList.id)
      },
      (circleItems) => {
        if (circleItems && circleItems.length > 0) {
          console.log(
            'Circle list items detected via watcher, dismissing splash'
          )
          stopWatching() // Stop watching once we have items
          markCircleListLoaded()
        }
      },
      { immediate: true }
    )

    console.log(
      'Splash screen management initialized for iOS - waiting for circle list'
    )
  }

  /**
   * Mark circle list as loaded and dismiss splash if ready
   */
  const markCircleListLoaded = () => {
    if (circleListLoaded.value) return // Already marked as loaded

    console.log('Circle list loaded, marking for splash dismissal')
    circleListLoaded.value = true

    // Small delay to ensure UI is ready
    setTimeout(() => {
      dismissSplash()
    }, 500)
  }

  /**
   * Dismiss the splash screen
   */
  const dismissSplash = async () => {
    if (splashDismissed.value) return // Already dismissed

    try {
      console.log('Dismissing splash screen...')

      // Hide custom spinner first
      showCustomSpinner.value = false

      // Small delay to ensure spinner fade out
      await new Promise((resolve) => setTimeout(resolve, 200))

      await SplashScreen.hide()
      splashDismissed.value = true

      // Clear timeout if it exists
      if (splashTimeout.value) {
        clearTimeout(splashTimeout.value)
        splashTimeout.value = null
      }
    } catch (error) {
      console.error('Error dismissing splash screen:', error)
    }
  }

  /**
   * Force dismiss splash screen (for error cases)
   */
  const forceDismissSplash = () => {
    console.log('Force dismissing splash screen...')
    dismissSplash()
  }

  return {
    // State
    splashDismissed: computed(() => splashDismissed.value),
    circleListLoaded: computed(() => circleListLoaded.value),
    canDismissSplash: computed(() => canDismissSplash.value),
    shouldManageSplash: computed(() => shouldManageSplash.value),
    showCustomSpinner: computed(() => showCustomSpinner.value),

    // Methods
    initSplashScreen,
    markCircleListLoaded,
    dismissSplash,
    forceDismissSplash
  }
}
